import type { ChannelDto, ChannelPostDto } from '@af/shared';
import type { LiveStatus } from './live-agents';

/** Sidebar order of agents (Slack-like presence): working first, then needs you, then available. */
const PRESENCE_ORDER: Record<LiveStatus, number> = { running: 0, approval: 1, failed: 2, idle: 3, paused: 4 };

export function sortByPresence<T extends { status: LiveStatus; agent: { name: string } }>(list: T[]): T[] {
  return [...list].sort(
    (a, b) => PRESENCE_ORDER[a.status] - PRESENCE_ORDER[b.status] || a.agent.name.localeCompare(b.agent.name),
  );
}

/** "Online" in the chat sidebar: an agent that can pick up work (not paused/archived). */
export const isOnline = (s: LiveStatus) => s !== 'paused';

/** Open channels and agent DMs for the sidebar, most recently active first. */
export function splitChannels(list: ChannelDto[]): { channels: ChannelDto[]; dms: ChannelDto[] } {
  const open = list
    .filter((c) => !c.archived)
    .sort((a, b) => (b.lastPostAt ?? b.createdAt).localeCompare(a.lastPostAt ?? a.createdAt));
  return { channels: open.filter((c) => c.kind === 'channel'), dms: open.filter((c) => c.kind === 'dm') };
}

/**
 * Channels agents open are temporary coordination rooms for one piece of work: they stay open for people
 * to follow until a person closes them (agents cannot).
 */
export const isTemporaryChannel = (c: Pick<ChannelDto, 'kind' | 'createdByAgentId'>) =>
  c.kind === 'channel' && !!c.createdByAgentId;

const GROUP_GAP_MS = 5 * 60_000;

/**
 * Slack-style grouping: a message continues the previous one (no name/avatar repeated) when it comes from
 * the same author within five minutes and neither is a platform line.
 */
export function withContinuation(posts: ChannelPostDto[]): { post: ChannelPostDto; continued: boolean }[] {
  return posts.map((post, i) => {
    const prev = posts[i - 1];
    const sameAuthor =
      !!prev &&
      prev.kind === 'message' &&
      post.kind === 'message' &&
      (prev.authorAgentId ?? prev.authorUserId) === (post.authorAgentId ?? post.authorUserId) &&
      Date.parse(post.createdAt) - Date.parse(prev.createdAt) < GROUP_GAP_MS;
    return { post, continued: sameAuthor };
  });
}

/** The @mention being typed at the caret (`@ha|` → `ha`), or null. */
export function mentionAt(text: string, caret: number): { query: string; start: number } | null {
  const m = /(?:^|[^\w@])@([a-z0-9-]{0,40})$/i.exec(text.slice(0, caret));
  if (!m) return null;
  return { query: m[1]!.toLowerCase(), start: caret - m[1]!.length - 1 };
}

/** Day label boundaries: true when `b` falls on another calendar day than `a`. */
export const newDay = (a: string | undefined, b: string) =>
  !a || new Date(a).toDateString() !== new Date(b).toDateString();
